import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Send, MessageCircle } from 'lucide-react';
import { isChatAvailable, getChatStatusMessage } from '@/utils/chatAvailability';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface Message {
  id: string;
  sender_type: 'user' | 'admin' | 'bot';
  message: string;
  created_at: string;
}

export function LiveChat() {
  const { user, effectiveUserId } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [offlineMessage, setOfflineMessage] = useState('');
  const [offlineName, setOfflineName] = useState('');
  const [offlineEmail, setOfflineEmail] = useState(user?.email || '');
  const scrollRef = useRef<HTMLDivElement>(null);
  const chatAvailable = isChatAvailable();

  // Get or create conversation
  const { data: conversation } = useQuery({
    queryKey: ['chat-conversation', effectiveUserId],
    queryFn: async () => {
      if (!effectiveUserId) return null;
      
      // Try to get existing active conversation
      const { data: existing } = await supabase
        .from('chat_conversations')
        .select('*')
        .eq('user_id', effectiveUserId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (existing) {
        setConversationId(existing.id);
        return existing;
      }

      // Create new conversation
      const { data: newConv, error } = await supabase
        .from('chat_conversations')
        .insert({ user_id: effectiveUserId })
        .select()
        .single();

      if (error) throw error;
      setConversationId(newConv.id);
      return newConv;
    },
    enabled: !!effectiveUserId && chatAvailable,
  });

  // Get messages
  const { data: messages = [] } = useQuery({
    queryKey: ['chat-messages', conversationId],
    queryFn: async () => {
      if (!conversationId) return [];
      
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as Message[];
    },
    enabled: !!conversationId,
    refetchInterval: 2000, // Poll every 2 seconds
  });

  // Listen to realtime updates
  useEffect(() => {
    if (!conversationId) return;

    const channel = supabase
      .channel('chat-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['chat-messages', conversationId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, queryClient]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessageMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!conversationId || !effectiveUserId) throw new Error('Not ready');

      const { error } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: conversationId,
          sender_id: effectiveUserId,
          sender_type: 'user',
          message: text,
        });

      if (error) throw error;

      // If chat is not available, trigger bot response
      if (!chatAvailable) {
        await supabase.functions.invoke('chat-bot-response', {
          body: { conversationId, userMessage: text }
        });
      }
    },
    onSuccess: () => {
      setMessage('');
      queryClient.invalidateQueries({ queryKey: ['chat-messages', conversationId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    },
  });

  const submitOfflineRequest = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('chat_offline_requests')
        .insert({
          user_id: effectiveUserId || null,
          email: offlineEmail,
          name: offlineName,
          message: offlineMessage,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Request Submitted",
        description: "We'll get back to you within 24 hours",
      });
      setOfflineMessage('');
      setOfflineName('');
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to submit request",
        variant: "destructive",
      });
    },
  });

  if (!chatAvailable) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Support Request
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {getChatStatusMessage()}. Please leave a message and we'll respond within 24 hours.
          </p>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              value={offlineName}
              onChange={(e) => setOfflineName(e.target.value)}
              placeholder="Your name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <Input
              type="email"
              value={offlineEmail}
              onChange={(e) => setOfflineEmail(e.target.value)}
              placeholder="your@email.com"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Message</label>
            <Textarea
              value={offlineMessage}
              onChange={(e) => setOfflineMessage(e.target.value)}
              placeholder="How can we help you?"
              rows={6}
            />
          </div>

          <Button 
            onClick={() => submitOfflineRequest.mutate()}
            disabled={!offlineEmail || !offlineMessage || submitOfflineRequest.isPending}
            className="w-full"
          >
            Submit Request
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto h-[600px] flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Live Chat
          <span className="ml-auto text-xs text-green-500 font-normal">● Online</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0">
        <ScrollArea className="flex-1 px-4" ref={scrollRef}>
          <div className="space-y-4 py-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg px-4 py-2 ${
                    msg.sender_type === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : msg.sender_type === 'bot'
                      ? 'bg-muted'
                      : 'bg-secondary'
                  }`}
                >
                  <p className="text-sm">{msg.message}</p>
                  <span className="text-xs opacity-70">
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && message && sendMessageMutation.mutate(message)}
              placeholder="Type your message..."
            />
            <Button
              onClick={() => message && sendMessageMutation.mutate(message)}
              disabled={!message || sendMessageMutation.isPending}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
